import { Ikon } from '@studio/design-system/button';
import { Focusable } from '@studio/design-system/focusable';
import { motion } from '@studio/design-system/motion';
import { useLatestFn } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import type { KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import { Combo } from './combo.jsx';
import { Container } from './container.jsx';
import { Editor } from './editor.jsx';
import { KeyCap } from './key-cap.jsx';
import type { MediatorProps } from './types.js';
import type { Combo as M_Combo } from '#~/hot-keys/index.js';

/** `<Mediator>` component. */
export const Mediator = ({
  className,
  onCancel,
  onChange,
  onCommit = noop,
  value,
  ...props
}: MediatorProps): ReactElement => {
  const [isEditing, setEditing] = useState(false);

  const handleCancel = useLatestFn(() => {
    onCancel?.();

    setEditing(false);
  });

  const handleCommit = useLatestFn((nextCombo: M_Combo | false) => {
    setEditing(false);

    onCommit(nextCombo);
  });

  const handleRemove = useLatestFn((event: MouseEvent) => {
    event.stopPropagation();

    onCommit(false);
  });

  const handleEdit = useLatestFn(() => {
    setEditing(true);
  });

  const handleKeyDown = useLatestFn((event: KeyboardEvent) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      onCommit(false);
    }
  });

  return (
    <motion.div layout>
      {!isEditing ? (
        <Focusable
          asChild
          className={twMerge(
            'flex cursor-pointer content-start items-center gap-4 overflow-hidden border border-transparent transition-colors hover:border-primary/50',
            className
          )}
          onClick={handleEdit}
          onKeyDown={handleKeyDown}
          title='Click to edit'
          {...props}
        >
          <Container asChild>
            <motion.div layout='position'>
              {value ? <Combo combo={value} /> : <KeyCap className='invisible' glyph={' '} />}

              <Ikon
                disabled={value === false}
                icon='remove'
                onClick={handleRemove}
                title='Remove hot key'
              />
            </motion.div>
          </Container>
        </Focusable>
      ) : (
        <Editor
          className={twMerge('h-[42px]', className)}
          focusOnMount
          onCancel={handleCancel}
          onChange={onChange}
          onCommit={handleCommit}
          {...props}
        />
      )}
    </motion.div>
  );
};
